import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  MessageCircle,
  AlertTriangle,
  Trash2,
  Plus,
  ChevronDown,
  Target,
  ListTodo,
} from 'lucide-react';
import { Button } from '@/shared/components/ui/Button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/shared/components/ui/Tooltip';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from '@/shared/components/ui/Select';
import { cn } from '@/shared/lib/utils';
import { UpdateGoalModal } from '@/features/goals/goal-form/UpdateGoalModal';
import { CreateGoalModal } from '@/features/goals/goal-form/CreateGoalModal';
import { CreateTaskModal } from '@/features/goals/goal-form/CreateTaskModal';
import { InteractionType, IssueStatus } from '@momentum/types';
import { useDeleteIssue } from '@/shared/hooks';
import { useUpdateGoal } from '@/shared/hooks/useGoals';
import { DeleteConfirmationDialog } from './DeleteConfirmationDialog';
import { InlineStatusSelector } from '@/features/one-on-one/goals-review/components/InlineStatusSelector';
import type { FullIssueWithChildren } from '@/shared/utils/issue-guards';
import { useIssuePermissions } from '@/features/goals/goal-form/hooks/useIssuePermissions';

interface GoalCardActionsProps {
  goal: FullIssueWithChildren;
  currentEmployeeId: string;
  interactionCount: number;
  descendantInteractionCount: number;
  blockagesCount: number;
  onShowInteractions: (types: InteractionType[]) => void;
}

export const GoalCardActions: React.FC<GoalCardActionsProps> = ({
  goal,
  interactionCount,
  descendantInteractionCount,
  blockagesCount,
  onShowInteractions,
}) => {
  const { t } = useTranslation('goals');
  const { canEdit, canDelete } = useIssuePermissions(goal);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showCreateGoalModal, setShowCreateGoalModal] = useState(false);
  const [showCreateTaskModal, setShowCreateTaskModal] = useState(false);
  const [selectKey, setSelectKey] = useState(0);
  const deleteIssueMutation = useDeleteIssue();
  const updateGoalMutation = useUpdateGoal();

  const handleStatusChange = useCallback(
    async (newStatus: IssueStatus) => {
      try {
        await updateGoalMutation.mutateAsync({
          id: goal._id,
          data: { status: newStatus },
        });
      } catch (error) {
        toast.error(t('failedToUpdateStatus'), {
          description:
            error instanceof Error
              ? error.message
              : t('couldNotUpdateGoalStatus'),
        });
      }
    },
    [goal._id, updateGoalMutation, t]
  );

  const handleDelete = async () => {
    try {
      await deleteIssueMutation.mutateAsync(goal._id);
      setShowDeleteDialog(false);
    } catch (error) {
      console.error('Failed to delete issue:', error);
    }
  };

  return (
    <>
      <DeleteConfirmationDialog
        open={showDeleteDialog}
        onOpenChange={setShowDeleteDialog}
        onConfirm={handleDelete}
        goalTitle={goal.title}
        isDeleting={deleteIssueMutation.isPending}
      />
      {showCreateGoalModal && (
        <CreateGoalModal
          parentGoalId={goal._id}
          open={showCreateGoalModal}
          onOpenChange={setShowCreateGoalModal}
        />
      )}
      {showCreateTaskModal && (
        <CreateTaskModal
          parentGoalId={goal._id}
          open={showCreateTaskModal}
          onOpenChange={setShowCreateTaskModal}
        />
      )}
      <div className="flex items-center gap-2 flex-shrink-0">
        {/* Create Nested Goal/Task Dropdown */}
        <div onClick={(e) => e.stopPropagation()}>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div>
                  <Select
                    key={selectKey}
                    onValueChange={(value) => {
                      setSelectKey((prev) => prev + 1);
                      if (value === 'goal') {
                        setShowCreateGoalModal(true);
                      } else if (value === 'task') {
                        setShowCreateTaskModal(true);
                      }
                    }}
                  >
                    <SelectTrigger
                      className={cn(
                        'h-8 gap-1 px-2.5 w-auto min-h-0',
                        'text-xs font-medium',
                        'border-gray-300 bg-white',
                        'text-gray-700 hover:text-blue-700',
                        'hover:border-blue-400 hover:bg-blue-50',
                        'transition-colors',
                        '[&>svg]:hidden' // Hide default chevron
                      )}
                    >
                      <div className="flex items-center gap-1">
                        <Plus className="h-3.5 w-3.5" />
                        <span>{t('addNestedItem')}</span>
                        <ChevronDown className="h-3.5 w-3.5 opacity-50" />
                      </div>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="goal" className="ps-2 cursor-pointer">
                        <span className="flex items-center gap-2">
                          <Target className="h-3.5 w-3.5" />
                          {t('addNestedGoal')}
                        </span>
                      </SelectItem>
                      <SelectItem value="task" className="ps-2 cursor-pointer">
                        <span className="flex items-center gap-2">
                          <ListTodo className="h-3.5 w-3.5" />
                          {t('addNestedTask')}
                        </span>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </TooltipTrigger>
              <TooltipContent side="top" className="text-xs">
                <p>{t('addNestedItemTooltip')}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        {/* Edit Button - Visible to owner or assignee */}
        {canEdit && <UpdateGoalModal goal={goal} />}
        {/* Delete Button - Only visible to owner */}
        {canDelete && (
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              setShowDeleteDialog(true);
            }}
            className="h-8 text-gray-600 hover:text-red-600 hover:bg-red-50"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
        {/* Interactions Button */}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            onShowInteractions([]);
          }}
          className="h-8 text-gray-600 hover:text-gray-900"
        >
          <MessageCircle className="h-4 w-4 me-1.5" />
          <span className="text-xs font-medium">
            {interactionCount > 0 ? (
              <>
                <span>{interactionCount}</span>
                {descendantInteractionCount > 0 ? (
                  <span className="text-gray-400">
                    {' '}
                    {t('fromSubGoals')(descendantInteractionCount)}
                  </span>
                ) : null}
              </>
            ) : descendantInteractionCount > 0 ? (
              <span className="text-gray-400">
                {t('fromSubGoalsLabel')(descendantInteractionCount)}
              </span>
            ) : (
              t('interactionsLabel')
            )}
          </span>
        </Button>

        {/* Blockages Badge */}
        {blockagesCount > 0 && (
          <div
            className={cn(
              'flex items-center gap-1.5 px-2.5 py-1.5',
              'rounded-lg border text-xs font-medium',
              'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100'
            )}
            onClick={() => {
              onShowInteractions([InteractionType.FEEDBACK]);
            }}
          >
            <AlertTriangle className="h-3.5 w-3.5" />
            <span>{t('blockagesLabel')(blockagesCount)}</span>
          </div>
        )}

        {/* Status Selector */}
        <div onClick={(e) => e.stopPropagation()}>
          <InlineStatusSelector
            value={goal.status}
            onValueChange={handleStatusChange}
            disabled={updateGoalMutation.isPending || !canEdit}
          />
        </div>
      </div>
    </>
  );
};
